import { isEmpty } from "@prismicio/editor-support/Object"
import type { Document } from "@prismicio/types-internal/lib/content"
import type {
  StaticCustomType,
  StaticSection
} from "@prismicio/types-internal/lib/customtypes"
import { useId } from "react"

import { type EditorConfig, EditorConfigContext } from "./EditorConfig"
import { RootFieldContextProvider } from "./FieldContext"
import { SectionEditor } from "./SectionEditor"

interface Props {
  config: EditorConfig
  readOnly?: boolean
  customType: StaticCustomType
  sectionName: string
  content: Document
  onContentChange: (content: Document) => void
}

export function DocumentEditor(props: Props) {
  const {
    config,
    readOnly = false,
    customType,
    sectionName,
    content,
    onContentChange
  } = props

  const id = useId()

  if (isEmpty(customType.json)) return null

  const section = getSection(customType, sectionName)
  const [sectionContent, restContent] = getSectionContent(section, content)

  return (
    <EditorConfigContext.Provider value={config}>
      <RootFieldContextProvider customTypeFormat={customType.format}>
        <SectionEditor
          id={id}
          readOnly={readOnly}
          section={section}
          content={sectionContent}
          onContentChange={(newSectionContent) =>
            onContentChange({ ...restContent, ...newSectionContent })
          }
        />
      </RootFieldContextProvider>
    </EditorConfigContext.Provider>
  )
}

function getSection(
  customType: StaticCustomType,
  sectionName: string
): StaticSection {
  const section = customType.json[sectionName]
  if (section === undefined) {
    throw new Error(`Cannot find a section with name '${sectionName}'.`)
  } else {
    return section
  }
}

function getSectionContent(
  section: StaticSection,
  documentContent: Document
): [Document, Document] {
  return Object.entries(documentContent).reduce(
    ([sectionContent, restContent], [key, value]) => {
      const isSection = section[key] !== undefined
      const entry = { [key]: value }
      return [
        { ...sectionContent, ...(isSection ? entry : {}) },
        { ...restContent, ...(isSection ? {} : entry) }
      ]
    },
    [{}, {}]
  )
}
